/**
 * anonymousHistoryStore — persistencia local del historial de conversaciones
 * para usuarios anónimos (Card iihkH2tK, F1).
 *
 * Patron estandar del mercado (ChatGPT/Claude/Gemini):
 *   - Al hacer "Nueva conversación" la convo activa se guarda al historial.
 *   - El sidebar lista las conversaciones guardadas y permite restaurarlas.
 *   - F1 solo cubre anónimos: el storage es localStorage del browser. F2
 *     (logueados) viajará por un backend dedicado y vivirá en otro módulo.
 *
 * Diseño:
 *   - Envelope versionado (card ejAQMKG2): el payload es
 *     `{ version, entries }` y la versión vive DENTRO del payload, no sólo en
 *     el nombre de la clave. El reader migra el formato legacy de `-v1` (un
 *     array desnudo) al envelope de `-v2` sin perder entries — ver
 *     `readEnvelope`.
 *   - SSR-safe: todos los accesos a `window.localStorage` van dentro de
 *     `typeof window !== 'undefined'` y un try/catch que maneja `SecurityError`
 *     en private mode. Si storage falla, el helper degrada silenciosamente:
 *     `listConversations()` devuelve `[]`, `save` no escribe pero tampoco
 *     tira la app, etc.
 *   - Dos topes de tamaño, ningún TTL (card ejAQMKG2 — la decisión y su razón
 *     están en `docs/changes/2026-10-09_ejaqmkg2-historial-localstorage.md`):
 *     cap FIFO de `HISTORY_MAX_ENTRIES` conversaciones y presupuesto de
 *     `HISTORY_MAX_BYTES` sobre el payload serializado. Un TTL borraría
 *     historial del usuario por el simple paso del tiempo, y no resolvería la
 *     copy congelada (la copy nace resuelta en el backend).
 *   - NO guardar conversaciones sin user input: el filtro vive en `chatUIStore`
 *     (donde sabemos si el último user msg es real o sintético), no acá.
 *
 * INVARIANTE (card ejAQMKG2, testeado en
 * `__tests__/anonymousHistoryStore.test.ts`): este store persiste la FUENTE,
 * nunca el render. Los text parts guardan markdown crudo y los data parts el
 * objeto del typed block tal como llegó del cable. Es lo que hace que un fix
 * del renderer (ej. `remark-breaks`, PR #783) alcance a los mensajes ya
 * guardados. Nunca escribir acá HTML ni strings derivados del DOM.
 *
 * Única excepción (card BDe1FCcc): un mensaje del usuario que el backend
 * redactó por traer un número de documento se guarda con el texto saneado
 * del servidor, nunca con el original — ver `redactUserMessage`.
 *
 * NO guarda:
 *   - Tokens Auth0 ni headers de transport.
 *   - `agent_meta` interno del backend.
 *   - `tool_call` parts (los typed-block data parts SÍ se guardan porque
 *     son el contenido visible del chat; los `tool` parts del SDK son
 *     traza interna y no deberían aparecer en messages user-visible).
 */

import type { UIMessage } from 'ai';

import { replaceUserMessageText } from '../utils/userMessageRedaction';

/**
 * Versión del shape persistido. Viaja DENTRO del payload (`{ version, entries }`)
 * para que un reader pueda decidir qué hacer sin inferirlo del nombre de la
 * clave. Bumpear junto con `HISTORY_STORAGE_KEY` y agregarle una rama a
 * `readEnvelope`.
 */
export const HISTORY_SCHEMA_VERSION = 2;

/** Clave vigente. El payload es un `HistoryEnvelope`. */
export const HISTORY_STORAGE_KEY = 'inbest-chat-history-v2';

/**
 * Clave legacy (v1). Su payload era un array desnudo de entries, sin envelope
 * ni campo de versión. `readEnvelope` la migra al leer y la borra SÓLO si el
 * read-back de `HISTORY_STORAGE_KEY` confirma la escritura.
 */
export const LEGACY_HISTORY_STORAGE_KEY_V1 = 'inbest-chat-history-v1';

/** Capacidad máxima (FIFO). Excede -> drop el más antiguo por `lastUpdatedAt`. */
export const HISTORY_MAX_ENTRIES = 20;

/**
 * Presupuesto de bytes del payload serializado. La cuota típica de
 * `localStorage` es ~5MB por origen y esta app la comparte con
 * `inbest.consent.v1`, `inbest.entry_channel.v1` e
 * `inbest-chat-conversation-id`. 2MB deja margen y 20 conversaciones no
 * deberían acercarse nunca.
 *
 * El shedding es oldest-first y se detiene con UNA entry: la conversación
 * activa no se descarta por presupuesto.
 */
export const HISTORY_MAX_BYTES = 2_000_000;

/** Máximo de caracteres del snippet del último user msg en la lista. */
export const SNIPPET_MAX_CHARS = 80;

/**
 * Una entry persistida en localStorage. Mantener este shape estable o
 * bumpear el key cuando cambie — el reader es estricto.
 */
export interface AnonymousConversationEntry {
  /** `conversationId` (UUID v4) de la sesión chat. Clave única en el cap. */
  id: string;
  /** Mensajes UIMessage tal como los expone `useChat` de AI SDK 6. */
  messages: UIMessage[];
  /** Último texto del user (truncado SNIPPET_MAX_CHARS). Vacío si N/A. */
  lastUserMessage: string;
  /** Epoch ms del último update — usado para sort + display "hace X". */
  lastUpdatedAt: number;
}

/**
 * True si `window.localStorage` está disponible. Único punto de chequeo SSR
 * (Vite build / Vercel SSR no debería renderizar este código, pero los tests
 * de jsdom y el primer paint de la SPA pueden disparar el lookup antes de
 * que el browser monte localStorage; defensivo).
 */
function isStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

/**
 * Payload persistido en `HISTORY_STORAGE_KEY`. El `version` viaja dentro del
 * JSON (no sólo en el nombre de la clave) para que un reader de otro build
 * pueda decidir qué hacer sin adivinar.
 */
interface HistoryEnvelope {
  version: number;
  entries: AnonymousConversationEntry[];
}

/** Lee y parsea una clave. `null` si falta, no parsea o storage falló. */
function readJson(key: string): unknown {
  if (!isStorageAvailable()) return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Extrae las entries válidas de un payload desconocido. Acepta las dos formas
 * que pueden aparecer en un browser real:
 *   - array desnudo (formato legacy v1, y defensa por si un build escribe así)
 *   - `{ version, entries }` (formato vigente)
 *
 * Una `version` MAYOR a la vigente se lee igual, best-effort: el shape de
 * entry es aditivo y descartar el historial de un build más nuevo sería peor
 * que ignorar los campos que no conocemos.
 *
 * Devuelve `null` cuando el payload no es ninguna de las dos formas — así el
 * caller distingue "no hay nada acá" de "hay un array vacío".
 */
function extractEntries(parsed: unknown): AnonymousConversationEntry[] | null {
  let candidates: unknown[];
  if (Array.isArray(parsed)) {
    candidates = parsed;
  } else if (
    parsed != null &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { entries?: unknown }).entries)
  ) {
    candidates = (parsed as { entries: unknown[] }).entries;
  } else {
    return null;
  }
  // Validación de shape entry por entry. Las inválidas se descartan
  // silenciosamente (defense in depth — un payload mal escrito por otra
  // pestaña no debería romper la sidebar).
  const valid: AnonymousConversationEntry[] = [];
  for (const item of candidates) {
    if (!isValidEntry(item)) continue;
    valid.push(item);
  }
  return valid;
}

/**
 * Lee el array completo de entries, migrando el formato legacy si hace falta.
 * Devuelve `[]` cuando storage no está disponible, no hay nada guardado, el
 * JSON está corrupto o el shape es inválido.
 *
 * Orden de resolución:
 *   1. `HISTORY_STORAGE_KEY` (envelope v2). Si tiene un payload legible, gana.
 *   2. `LEGACY_HISTORY_STORAGE_KEY_V1` (array desnudo). Si tiene entries, se
 *      migran: se escribe el envelope v2 y la clave legacy se borra SÓLO si el
 *      read-back confirma que el v2 quedó escrito. Si el write falló (cuota,
 *      private mode), la legacy NO se toca y queda como única copia.
 *
 * NO ordena ni filtra acá — esa responsabilidad es de `listConversations`.
 * Mantener este reader puro hace fácil testear y compartirlo entre callers
 * sin sorpresas (ej. `removeConversation` necesita el array crudo).
 */
function readAllRaw(): AnonymousConversationEntry[] {
  if (!isStorageAvailable()) return [];

  const current = extractEntries(readJson(HISTORY_STORAGE_KEY));
  if (current !== null) return current;

  // No hay v2 legible — intentamos la clave legacy y migramos.
  const legacy = extractEntries(readJson(LEGACY_HISTORY_STORAGE_KEY_V1));
  if (legacy === null || legacy.length === 0) return [];

  if (writeAll(legacy) && extractEntries(readJson(HISTORY_STORAGE_KEY)) !== null) {
    try {
      window.localStorage.removeItem(LEGACY_HISTORY_STORAGE_KEY_V1);
    } catch {
      // Si no podemos borrar la legacy no pasa nada: el reader prefiere v2.
    }
  }
  return legacy;
}

function isValidEntry(item: unknown): item is AnonymousConversationEntry {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  if (typeof obj.id !== 'string' || obj.id.length === 0) return false;
  if (!Array.isArray(obj.messages)) return false;
  if (typeof obj.lastUserMessage !== 'string') return false;
  if (typeof obj.lastUpdatedAt !== 'number' || !Number.isFinite(obj.lastUpdatedAt)) {
    return false;
  }
  return true;
}

/** Serializa el envelope vigente. Es el único lugar que escribe `version`. */
function serializeEnvelope(entries: AnonymousConversationEntry[]): string {
  const envelope: HistoryEnvelope = {
    version: HISTORY_SCHEMA_VERSION,
    entries,
  };
  return JSON.stringify(envelope);
}

/**
 * Escribe el envelope completo al localStorage. Devuelve `true` si el
 * `setItem` no tiró — `false` si storage no está disponible o si ni con una
 * sola entry entra.
 *
 * Dos defensas contra el crecimiento sin tope (card ejAQMKG2):
 *
 *   1. **Presupuesto previo**: si el payload serializado supera
 *      `HISTORY_MAX_BYTES`, se descartan entries oldest-first antes de
 *      intentar el write. La medida es `String.length` del JSON (unidades
 *      UTF-16), que es un piso del tamaño real en bytes — suficiente para un
 *      presupuesto con el margen que tiene éste.
 *   2. **Retry con shedding**: si el `setItem` igual tira (`QuotaExceededError`
 *      — la cuota es del origen entero, no de esta clave), se descarta la
 *      entry más vieja y se reintenta. Antes el error se silenciaba y el valor
 *      ANTERIOR quedaba en su lugar: el usuario seguía viendo un snapshot
 *      viejo repintado y sin ninguna señal.
 *
 * El shedding nunca baja de una entry: la conversación activa no se descarta
 * por presupuesto. Si ni esa entra, se degrada silenciosamente — el state en
 * memoria del store sigue OK.
 */
function writeAll(entries: AnonymousConversationEntry[]): boolean {
  if (!isStorageAvailable()) return false;
  // Orden descendente por recencia para que el shedding corte por la cola
  // (la entry más vieja). Copia propia: no mutamos el array del caller.
  let candidate = [...entries].sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt);
  while (
    candidate.length > 1 &&
    serializeEnvelope(candidate).length > HISTORY_MAX_BYTES
  ) {
    candidate = candidate.slice(0, candidate.length - 1);
  }
  for (;;) {
    try {
      window.localStorage.setItem(HISTORY_STORAGE_KEY, serializeEnvelope(candidate));
      return true;
    } catch {
      // QuotaExceededError / SecurityError / DOMException.
      if (candidate.length <= 1) return false;
      candidate = candidate.slice(0, candidate.length - 1);
    }
  }
}

/**
 * Extrae el último texto del user de los messages. Usado para el snippet
 * de la lista en el sidebar. Devuelve string vacío si no hay user turn con
 * texto visible.
 *
 * Recorre `messages` de atrás hacia adelante buscando el primer turn cuyo
 * role sea `'user'` con al menos un text part. Mantiene paridad con el
 * filtro de `ChatPanelV2` (los synthetic prefixes ya vienen excluidos del
 * snapshot por el caller, ver `chatUIStore.startNewConversation`).
 */
export function extractLastUserText(messages: UIMessage[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg?.role !== 'user') continue;
    const parts = Array.isArray(msg.parts) ? msg.parts : [];
    const textPart = parts.find(
      (p) =>
        p != null &&
        typeof p === 'object' &&
        (p as { type?: unknown }).type === 'text' &&
        typeof (p as { text?: unknown }).text === 'string',
    ) as { text: string } | undefined;
    if (textPart) {
      const trimmed = textPart.text.trim();
      if (trimmed.length === 0) continue;
      return trimmed.length > SNIPPET_MAX_CHARS
        ? `${trimmed.slice(0, SNIPPET_MAX_CHARS - 1)}…`
        : trimmed;
    }
  }
  return '';
}

/**
 * Cuenta cuántos turns son del user con texto visible. Lo usa `chatUIStore`
 * para decidir si vale la pena guardar la convo (no guardar si solo greeting
 * o wizard intro).
 */
export function countUserTextMessages(messages: UIMessage[]): number {
  let count = 0;
  for (const msg of messages) {
    if (msg?.role !== 'user') continue;
    const parts = Array.isArray(msg.parts) ? msg.parts : [];
    const hasText = parts.some(
      (p) =>
        p != null &&
        typeof p === 'object' &&
        (p as { type?: unknown }).type === 'text' &&
        typeof (p as { text?: unknown }).text === 'string' &&
        (p as { text: string }).text.trim().length > 0,
    );
    if (hasText) count++;
  }
  return count;
}

/**
 * Lista todas las conversaciones guardadas, ordenadas más-reciente primero.
 * Esta es la API que consume `SidebarHistory` para renderizar la lista.
 */
export function listConversations(): AnonymousConversationEntry[] {
  const all = readAllRaw();
  // Sort descendente por `lastUpdatedAt`. Mutamos la copia que devuelve
  // `readAllRaw` (es propia, no compartida con el caller).
  all.sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt);
  return all;
}

/**
 * Lee UNA conversación por id. Devuelve `null` si no existe o el storage
 * está deshabilitado.
 */
export function loadConversation(id: string): AnonymousConversationEntry | null {
  if (!id) return null;
  const all = readAllRaw();
  return all.find((entry) => entry.id === id) ?? null;
}

/**
 * Guarda (UPSERT) una conversación. Si ya existía con el mismo id, se
 * actualizan messages + snippet + timestamp (queda primero por sort).
 *
 * Cap FIFO: si tras el upsert el total supera `HISTORY_MAX_ENTRIES`, se
 * elimina la entry con `lastUpdatedAt` más viejo (no la que estamos
 * guardando aunque sea más vieja — el UPSERT le pone timestamp `Date.now()`).
 *
 * Para no guardar conversaciones vacías el caller debe chequear
 * `countUserTextMessages(messages) >= 1` antes de invocar.
 */
export interface SaveConversationOptions {
  /** Override del timestamp (tests). Default: `Date.now()`. */
  now?: number;
}

export function saveConversation(
  id: string,
  messages: UIMessage[],
  options: SaveConversationOptions = {},
): void {
  if (!id || !Array.isArray(messages)) return;
  const now = options.now ?? Date.now();
  const lastUserMessage = extractLastUserText(messages);

  const all = readAllRaw();
  // UPSERT por id.
  const filtered = all.filter((entry) => entry.id !== id);
  const next: AnonymousConversationEntry = {
    id,
    messages,
    lastUserMessage,
    lastUpdatedAt: now,
  };
  filtered.push(next);

  // Cap FIFO: si superamos el límite, eliminamos las entradas más viejas.
  // Ordenamos desc por lastUpdatedAt y nos quedamos con los primeros N.
  filtered.sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt);
  const capped = filtered.slice(0, HISTORY_MAX_ENTRIES);
  writeAll(capped);
}

/**
 * Card BDe1FCcc — reemplaza el texto de un mensaje del USUARIO ya persistido
 * por el texto saneado que mandó el backend (chunk
 * `data-user-message-redacted`: el mensaje traía un número de documento).
 *
 * Es la excepción documentada a «persiste la FUENTE»: la fuente de ese
 * mensaje pasa a ser lo que guardó el servidor, no lo que escribió el
 * usuario. Recalcula el snippet y conserva `lastUpdatedAt` (no reordena la
 * lista). No-op si la conversación o el mensaje del usuario no existen.
 */
export function redactUserMessage(
  conversationId: string,
  messageId: string,
  text: string,
): void {
  if (!conversationId || !messageId) return;
  const all = readAllRaw();
  const entry = all.find((item) => item.id === conversationId);
  if (!entry) return;
  const index = entry.messages.findIndex(
    (msg) => msg?.role === 'user' && msg.id === messageId,
  );
  if (index < 0) return;
  const messages = [...entry.messages];
  messages[index] = replaceUserMessageText(messages[index], text);
  const next: AnonymousConversationEntry = {
    ...entry,
    messages,
    lastUserMessage: extractLastUserText(messages),
  };
  writeAll(all.map((item) => (item.id === conversationId ? next : item)));
}

/**
 * Elimina una conversación del historial. No-op si no existe.
 */
export function removeConversation(id: string): void {
  if (!id) return;
  const all = readAllRaw();
  const filtered = all.filter((entry) => entry.id !== id);
  if (filtered.length === all.length) return; // nothing changed
  writeAll(filtered);
}

/**
 * Limpia el historial completo. No expuesto en UI todavía — útil para tests
 * y para una futura acción "Borrar historial" del sidebar.
 *
 * Borra TAMBIÉN la clave legacy: si quedara, el próximo `readAllRaw` la
 * migraría y le resucitaría el historial al usuario que acaba de borrarlo.
 */
export function clearHistory(): void {
  if (!isStorageAvailable()) return;
  for (const key of [HISTORY_STORAGE_KEY, LEGACY_HISTORY_STORAGE_KEY_V1]) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Silenciosamente OK.
    }
  }
}
